import React, { useState, useEffect, useRef } from 'react';
import SearchIcon from '../../assets/images/Search1.svg';
import NotesIcon from '../../assets/images/Notes.svg';
import DownArrowIcon from '../../assets/images/DownArrowIcon.svg';
import ActionsIcon from '../../assets/images/Actions.svg';
import BookmarkLibraryIcon from '../../assets/images/BookmarkLibraryIcon.svg';
import AnnotationIcon from '../../assets/images/Annotation.svg';
import LessonPlannerIcon from '../../assets/images/LessonPlanner.svg';
import AddNoteIcon from '../../assets/images/AddNote.svg';
import ViewNotesIcon from '../../assets/images/ViewNote.svg';
import SearchModal from './SearchModal';
import AnnotationModal from './AnnotationModal';
import AnnotationLibraryModal from './AnnotationLibraryModal';
import {
    addNoteIcon,
    captureSelection,
    getCfiOnClick,
    highlightText,
    removeNoteIcon,
} from '../../utils/readiumUtils';
import AnnotationSaveModal from './AnnotationSaveModal';
import {
    useAddHighlightMutation,
    useGetHighlightsQuery,
} from '../../api/highlights';
import {
    useAddBookmarkMutation,
    useDeleteBookmarkMutation,
} from '../../api/bookmark';
import NotesLibraryModal from './NotesLibraryModal';
import BookmarkLibraryModal from './BookmarkLibraryModal';
import AddNoteModal from './AddNoteModal';
import { useAddNoteMutation, useDeleteNoteMutation } from '../../api/note';
import ColorModal from './ColorModal';
import NoteIcon from './NoteIcon';
import store, { RootState } from '../../store';
import { useDispatch, useSelector } from 'react-redux';
import { appendNote, removeNote } from '../../features/notes/noteSlice';
import {
    appendBookmark,
    removeBookmark,
} from '../../features/bookMark/bookMarkSlice';
import { appendHighlight } from '../../features/highlights/highlightsSlice';
import { getTOC } from '../../helper';
import { setTOCData } from '../../features/initialTocData/initialTocDataSlice';
import { notification } from 'antd';
import { trackEvent } from '../../segment';
interface TOCState {
    pages: number[]; // Sorted array of page numbers for binary search
    chapters: { [page: number]: string }; // Mapping from page number to chapter title
}

interface ActionProps {
    bookUri: string;
    offset: number;
    bookId: string;
}
interface Item {
    icon_path: string;
    description: string;
    handleClick?: () => void;
    subItems?: {
        icon_path: string;
        description: string;
        handleClick: () => void;
    }[];
}

const Actions: React.FC<ActionProps> = ({ bookUri, offset, bookId }) => {
    const dispatch = useDispatch();
    //Action menu states
    const [isOpen, setIsOpen] = useState(false);
    const [activeItem, setActiveItem] = useState<string | null>(null);

    //Search modal state
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    //Annotation or Highlight realted States
    const [isAnnotationModalOpen, setIsAnnotationModalOpen] = useState(false);
    const [isAnnotationLibraryOpen, setIsAnnotationLibraryOpen] =
        useState(false);
    const [isAnnotationSaveModalOpen, setIsAnnotationSaveModalOpen] =
        useState(false);
    const [annotationCfi, setAnnotationCfi] = useState<any>(null);
    const [isColorModalOpen, setIsColorModalOpen] = useState(false);
    const [
        colorModalPositionParameterObject,
        setColorModalPositionParameterObject,
    ] = useState({
        bottom: 0,
        height: 0,
        left: 0,
        right: 0,
        top: 0,
        width: 0,
        x: 0,
        y: 0,
    });
    const [selectedText, setSelectedText] = useState('');
    const [currentColor, setCurrentColor] = useState('yellow');

    //Notes library modal states
    const [noteClickedState, setNoteClickedState] = useState({
        top: 0,
        left: 0,
        noteText: '',
        noteId: 0,
        bookId: '',
        pageNo: 0,
    });
    const [isNotesLibraryModalOpen, setIsNotesLibraryModalOpen] =
        useState(false);
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
    const [tapNoteCfi, setTapNoteCfi] = useState({ contentCFI: '', idref: '' });
    const [noteProperties, setNoteProperties] = useState({
        left: 0,
        top: 0,
    });

    //Bookmark library modal states
    const [isBookmarkLibraryModalOpen, setIsBookmarkLibraryModalOpen] =
        useState(false);

    //common states
    const [isSubItemMenuOpen, setIsSubItemMenuOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(-1);
    const [currentHref, setCurrentHref] = useState('');

    //note
    const [currentPageNotes, setCurrentPageNotes] = useState<any>();

    //api call mutations
    const [addBookMark] = useAddBookmarkMutation();

    const [addHighlight] = useAddHighlightMutation();

    const [addNote] = useAddNoteMutation();
    const [deleteBookmark, { isSuccess: isDeleteSuccess }] =
        useDeleteBookmarkMutation();

    const handleDeleteBookmark = async (bookmarkData: any[]) => {
        if (!bookmarkData || bookmarkData.length === 0) {
            console.error('No bookmark data found to delete.');
            return;
        }

        const bookmark = bookmarkData[0];
        if (!bookmark.id) {
            notification.error({
                message: 'Error',
                description: 'Invalid bookmark data: missing ID.',
                duration: 3,
            });
            return;
        }

        try {
            const id = bookmark.id;
            dispatch(removeBookmark(id));

            await deleteBookmark({
                id: id,
                bookId: bookId,
            }).unwrap();
        } catch (error) {
            console.error('Failed to delete bookmark:', error);
        }
    };

    const allBookMarks = useSelector(
        (state: RootState) => state.bookmarks.bookmarks
    );
    const allNotes = useSelector((state: RootState) => state.notes.notes);
    const allAnnotations = useSelector(
        (state: RootState) => state.highlights.highlights
    );

    const [deleteNote, { isSuccess: isNoteDeleteSuccess }] =
        useDeleteNoteMutation();

    // Function to search for a chapter name by page number
    const getChapterNameByPage = (
        state: TOCState,
        pageNumber: number
    ): string => {
        const index = lowerBound(state.pages, pageNumber);
        return index !== -1 ? state.chapters[state.pages[index]] : '';
    };

    // Lower Bound Function
    //returns index of the largest page number that is less than or equal to the target
    const lowerBound = (array: number[], target: number): number => {
        let low = 0;
        let high = array.length - 1;
        let result = -1;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);

            if (array[mid] <= target) {
                result = mid;
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        return result;
    };

    const handleNoteDelete = async (id: number) => {
        try {
            dispatch(removeNote(id));

            await deleteNote({ id: id, bookId: bookId });
            removeNoteIcon(window, id);
        } catch (error) {
            console.error('Failed to delete note:', error);
        }
    };

    useEffect(() => {
        const fetchTOC = async () => {
            const res = await getTOC(bookUri);

            dispatch(setTOCData(res));
        };
        fetchTOC();
    }, [bookUri, offset]);
    const initialDataToc = useSelector((state: RootState) => state.initialTOC);

    useEffect(() => {
        if (currentPageNotes?.length > 0) {
            addNoteIcon(window, currentPageNotes);
        }
    }, [currentPageNotes]);
    useEffect(() => {
        const handleActions = (event: MessageEvent) => {
            if (event.data.type === 'selection') {
                setColorModalPositionParameterObject(event.data.colourPallete);
                setSelectedText(event.data.selectedText);
                setAnnotationCfi(event.data.cfi);
                setIsColorModalOpen(true);
                setCurrentHref(event.data.href);
                setCurrentPage(event.data.page_no - offset);
            } else if (event.data.type === 'tap') {
                setTapNoteCfi(event.data.cfi);
                setNoteProperties(event.data.properties);
                setIsAddNoteModalOpen(true);
                setCurrentHref(event.data.href);
                setCurrentPage(event.data.page_no);
            } else if (event.data.type == 'bookmark') {
                const chapterName = getChapterNameByPage(
                    initialDataToc,
                    event.data.page_no
                );
                const bookmark = {
                    book_id: bookId,
                    href: event.data.href,
                    page_no: event.data.page_no - offset,
                    cfi: event.data.cfi,
                    chapter_name: chapterName,
                };

                addBookMark(bookmark)
                    .unwrap()
                    .then((response) => {
                        const newBookMark = response?.data;
                        dispatch(appendBookmark(newBookMark));
                    })
                    .catch((error) => {
                        console.error('Error saving bookmark:', error);
                    });
            } else if (event.data.type == 'unbookmark') {
                const href = event.data.href;
                const unBookmarkData = allBookMarks?.filter(
                    (bookmark: any) => bookmark.href == href
                );
                handleDeleteBookmark(unBookmarkData);
            } else if (event.data.type == 'iframeLoaded') {
                getCurrentPageHighlights(event.data.pageData);
                applyCurrentPageBookmarks(event.data.pageData);
                getCurrentPageNotes(event.data.pageData);
            } else if (event.data.type == 'notes') {
                setNoteClickedState({
                    top: event?.data?.properties?.top,
                    left: event?.data?.properties?.left,
                    noteText: event?.data?.noteText,
                    noteId: event?.data?.noteId,
                    bookId: bookId,
                    pageNo: event?.data?.page_no,
                });
            }
        };

        window.addEventListener('message', handleActions);
        return () => {
            window.removeEventListener('message', handleActions);
        };
    });

    const applyCurrentPageNotes = (currentPageNotesArray: any) => {
        setCurrentPageNotes(currentPageNotesArray);
    };
    const applyCurrentPageBookmarks = (
        pageData: Array<{ href: string; page_no: number }>
    ) => {
        const currentPageBookmarks = allBookMarks?.filter(
            (currentBookmark: { href: string }) =>
                pageData.some((page) => page.href === currentBookmark.href)
        );
        if (currentPageBookmarks && currentPageBookmarks.length !== 0) {
            currentPageBookmarks.forEach((currentBookmark) => {
                window[0]?.updateBookmarkIcon(currentBookmark.href, true);
            });
        }
    };
    const applyCurrentPageHighlights = (currentPageHiglightsArray: any) => {
        currentPageHiglightsArray?.map((currentHighlight: any) => {
            const cfi = currentHighlight?.cfi;
            const currentColour =
                typeof currentHighlight?.properties === 'string'
                    ? JSON.parse(currentHighlight.properties)?.colour
                    : currentHighlight?.properties?.colour;

            highlightText(window, cfi, currentColour, currentHighlight?.id);
        });
    };
    const getCurrentPageHighlights = (
        pageData: Array<{ href: string; page_no: number }>
    ) => {
        const currentPageHighlightsArray = allAnnotations?.filter(
            (currentAnnotation: { href: string }) =>
                pageData.some((page) => page.href === currentAnnotation.href)
        );
        applyCurrentPageHighlights(currentPageHighlightsArray);
    };
    const getCurrentPageNotes = (
        pageData: Array<{ href: string; page_no: number }>
    ) => {
        const currentPageNotes = allNotes?.filter(
            (currentNote: { href: string }) =>
                pageData?.some((page) => page.href === currentNote.href)
        );
        if (currentPageNotes) {
            applyCurrentPageNotes(currentPageNotes);
        }
    };

    const handleSubItemMenuClicked = () => {
        setIsSubItemMenuOpen(!isSubItemMenuOpen);
    };
    const handleAnnotationLibraryClick = () => {
        trackEvent('click_action_view_highlights', {});
        setIsAnnotationLibraryOpen(!isAnnotationLibraryOpen);
    };

    const handleAddAnnotationClick = () => {
        trackEvent('click_action_add_highlights', {});
        setIsAnnotationModalOpen(true);
    };
    const handleAnnotationModalOkayClick = () => {
        setIsAnnotationModalOpen(false);

        captureSelection(window, 'true');
    };
    const handleAnnotationModalClose = () => {
        setIsAnnotationModalOpen(false);
    };

    const handleActionsButtonClicked = () => {
        setIsOpen(!isOpen);
    };
    const handleAnnotationSaveModalOkayClick = () => {
        const chapterName = getChapterNameByPage(initialDataToc, currentPage);

        const highlight = {
            book_id: bookId,
            page_no: currentPage,
            selection:
                selectedText.length > 30
                    ? selectedText.slice(0, 50) + ' ...'
                    : selectedText,
            href: currentHref,
            cfi: annotationCfi,
            properties: {
                colour: currentColor,
                underline: true,
            },
            chapter_name: chapterName,
        };

        addHighlight(highlight)
            .unwrap()
            .then((response: any) => {
                if (response) {
                    highlightText(
                        window,
                        annotationCfi,
                        currentColor,
                        response?.data?.id
                    );
                    dispatch(appendHighlight(response?.data));
                }
            })
            .catch((error) => {
                console.error('Error saving highlight:', error);
            });

        setIsAnnotationSaveModalOpen(false);
        captureSelection(window, 'false');
    };
    const handleAnnotationSaveModalCloseClicked = () => {
        setIsAnnotationSaveModalOpen(false);
        captureSelection(window, 'false');
    };

    const handleColorChangeClick = (color: string) => {
        setCurrentColor(color);
        setIsSearchModalOpen(false);
        setIsColorModalOpen(false);

        captureSelection(window, 'true');
        setIsAnnotationSaveModalOpen(true);
    };

    //note functions
    const handleNotesModalOpenOkayClicked = () => {
        setIsNotesModalOpen(false);

        getCfiOnClick(window, 'true');
    };

    const handleNoteSaveClick = (noteText: string) => {
        const chapterName = getChapterNameByPage(initialDataToc, currentPage);
        const note = {
            book_id: bookId,
            page_no: currentPage,
            href: currentHref,
            text: noteText,
            cfi: tapNoteCfi,
            properties: noteProperties,
            chapter_name: chapterName,
        };
        addNote(note)
            .unwrap()
            .then((response) => {
                if (response) {
                    const newNote = response?.data;
                    dispatch(appendNote(newNote));
                    const updatedNotes = store.getState().notes?.notes;
                    addNoteIcon(window, updatedNotes);
                }
            })
            .catch((error) => {
                console.error('Error saving note:', error);
            });
        setIsAddNoteModalOpen(false);
        getCfiOnClick(window, 'false');
    };
    const handleAddNoteCancel = () => {
        setIsAddNoteModalOpen(false);
        getCfiOnClick(window, 'false');
    };
    const handleItemClick = (
        e: React.MouseEvent,
        item: any,
        activeItem: string | null,
        setActiveItem: (value: string | null) => void,
        handleSubItemMenuClicked: () => void
    ) => {
        e.stopPropagation(); // Prevent parent click event

        if (item.subItems) {
            // Toggle sub-items visibility
            setActiveItem(
                activeItem === item.description ? null : item.description
            );
            handleSubItemMenuClicked();
        } else {
            // Handle the click for non-subItems
            item.handleClick?.();
        }
    };

    const items: Item[] = [
        {
            icon_path: SearchIcon,
            description: 'Search',
            handleClick: () => {
                setIsSearchModalOpen(true);
                trackEvent('click_action_search', {});
            },
        },
        {
            icon_path: NotesIcon,
            description: 'Notes',
            subItems: [
                {
                    icon_path: AddNoteIcon,
                    description: 'Add Note',
                    handleClick: () => {
                        setIsNotesModalOpen(true);
                        trackEvent('click_action_add_note', {});
                    },
                },
                {
                    icon_path: ViewNotesIcon,
                    description: 'View Notes',
                    handleClick: () => {
                        setIsNotesLibraryModalOpen(true);
                        trackEvent('click_action_view_note', {});
                    },
                },
            ],
        },
        {
            icon_path: AnnotationIcon,
            description: 'Annotation',
            subItems: [
                {
                    icon_path: AddNoteIcon,
                    description: 'Add Highlight',
                    handleClick: handleAddAnnotationClick,
                },
                {
                    icon_path: ViewNotesIcon,
                    description: 'View Highlights',
                    handleClick: handleAnnotationLibraryClick,
                },
            ],
        },
        {
            icon_path: BookmarkLibraryIcon,
            description: 'Bookmark Library',
            handleClick: () => {
                setIsBookmarkLibraryModalOpen(true);
                trackEvent('click_action_view_bookmark', {});
            },
        },
        // {
        //     icon_path: LessonPlannerIcon,
        //     description: 'Lesson planner',
        //     handleClick: () => {},
        // },
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setActiveItem(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <>
            <div
                ref={dropdownRef}
                className="z-30 hover:cursor-pointer h-12 w-12 flex flex-row-reverse items-end"
                onClick={handleActionsButtonClicked}
            >
                <div className="flex flex-col bg-white rounded-md min-w-[3.125rem] h-[3.125rem] items-center justify-center -space-y-1">
                    <img
                        src={ActionsIcon}
                        alt="view"
                        className="h-[1.6rem] w-[1.6rem]"
                    />
                    <p className="text-[#7B86A0] font-semibold text-xs">
                        Actions
                    </p>
                </div>
                {isOpen && (
                    <div className="flex flex-col rounded-xl bg-white h-auto w-auto px-[0.625rem] py-[1rem] shadow-lg mr-1 justify-center">
                        {items?.map((item, index) => (
                            <div
                                key={index}
                                className="flex flex-col items-center justify-center"
                            >
                                <div
                                    className="flex h-[2.375rem] w-[13.063rem] items-center justify-start hover:bg-[#EDF1F9] py-[0.813rem] "
                                    onClick={(e) =>
                                        handleItemClick(
                                            e,
                                            item,
                                            activeItem,
                                            setActiveItem,
                                            handleSubItemMenuClicked
                                        )
                                    }
                                >
                                    <div className="w-full flex flex-row">
                                        <div className="w-full flex items-center">
                                            <img
                                                src={item.icon_path}
                                                alt={item.description}
                                                className="h-[1.5rem] w-[1.5rem] ml-5"
                                            />
                                            <p className="text-nowrap ml-[0.75rem] mr-5 text-xs font-sans ">
                                                {item.description}
                                            </p>
                                        </div>
                                        {item.subItems && (
                                            <button
                                                className="pr-2"
                                                data-testid="actions-submenu-drop-down-button"
                                            >
                                                <img
                                                    src={DownArrowIcon}
                                                    alt="DownArrowIcon"
                                                />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {item.subItems &&
                                    activeItem === item.description && (
                                        <div className="ml-10 border border-black rounded-xl py-2 w-[10rem] border-opacity-5">
                                            {item.subItems.map(
                                                (subItem, subIndex) => (
                                                    <div
                                                        key={subIndex}
                                                        className="flex h-[2rem] w-[8.9rem] items-center justify-start hover:bg-[#EDF1F9] py-[0.5rem] ml-4 px-2"
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent parent click event
                                                            subItem.handleClick();
                                                        }}
                                                    >
                                                        <img
                                                            src={
                                                                subItem.icon_path
                                                            }
                                                            alt={
                                                                subItem.description
                                                            }
                                                            className="h-[1.2rem] w-[1.2rem] mr-1"
                                                        />
                                                        <p className="text-nowrap text-xs font-sans ">
                                                            {
                                                                subItem.description
                                                            }
                                                        </p>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Search Modal Section  */}
            {isSearchModalOpen && (
                <SearchModal
                    onClose={() => setIsSearchModalOpen(false)}
                    bookUri={bookUri}
                    offset={offset}
                />
            )}
            {/* Annotations Section  */}
            {isAnnotationModalOpen && (
                <AnnotationModal
                    onClose={handleAnnotationModalClose}
                    onOkay={handleAnnotationModalOkayClick}
                    message="Please select some text which you want to add annotation!"
                />
            )}
            {isAnnotationSaveModalOpen && (
                <AnnotationSaveModal
                    onOkay={handleAnnotationSaveModalOkayClick}
                    onClose={handleAnnotationSaveModalCloseClicked}
                />
            )}
            {isAnnotationLibraryOpen && (
                <AnnotationLibraryModal
                    onClose={() => setIsAnnotationLibraryOpen(false)}
                    bookId={bookId}
                    offset={offset}
                />
            )}
            {isColorModalOpen && (
                <ColorModal
                    changeColor={handleColorChangeClick}
                    ColorModalPositionParameters={
                        colorModalPositionParameterObject
                    }
                />
            )}
            {/* Notes section  */}
            {isNotesLibraryModalOpen && (
                <NotesLibraryModal
                    onClose={() => setIsNotesLibraryModalOpen(false)}
                    bookId={bookId}
                    isNotesLibraryModalOpen={isNotesLibraryModalOpen}
                    offset={offset}
                />
            )}
            {isNotesModalOpen && (
                <AnnotationModal
                    onOkay={handleNotesModalOpenOkayClicked}
                    onClose={() => setIsNotesModalOpen(false)}
                    message="Click or tap any where on the page to add note."
                />
            )}
            {isAddNoteModalOpen && (
                <AddNoteModal
                    onClose={handleAddNoteCancel}
                    onSave={handleNoteSaveClick}
                />
            )}

            {noteClickedState && (
                <NoteIcon
                    noteText={noteClickedState?.noteText}
                    noteId={noteClickedState?.noteId}
                    bookId={bookId}
                    handleDelete={() =>
                        handleNoteDelete(noteClickedState?.noteId)
                    }
                />
            )}

            {/* Bookmark Section  */}
            {isBookmarkLibraryModalOpen && (
                <BookmarkLibraryModal
                    onClose={() => setIsBookmarkLibraryModalOpen(false)}
                    bookId={bookId}
                    isBookmarkLibraryModalOpen={isBookmarkLibraryModalOpen}
                    offset={offset}
                />
            )}
        </>
    );
};

export default Actions;
